import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import trackerRedux from '@openreplay/tracker-redux';
import rootSaga from './root-saga';
import createReducerManager from './reducer-manager';
import createSagaInjector from './saga-manager';
import SliceManager from './slice-manager';
import { logInDev } from '../utils/log-utils';
import { EnvTypes } from '../utils/base-url-util';
import analyticsTrackerInstance from '../../analytics';

export default (initialState, initialReducers) => {
  const reducerManager = createReducerManager(initialReducers);

  const sagaMiddleware = createSagaMiddleware({
    onError: (error) => {
      logInDev('Saga Root Destroyed', error);
    },
  });

  const openReplayMiddleware = analyticsTrackerInstance.tracker?.use(trackerRedux());
  const store = configureStore({
    reducer: reducerManager.getCombinedReducers(),
    middleware: (getDefaultMiddleware) => (
      [
        ...getDefaultMiddleware({ thunk: false, serializableCheck: false }),
        sagaMiddleware,
        ...(openReplayMiddleware ? [openReplayMiddleware] : []),
      ]
    ),
    devTools: process.env.REACT_APP_STAGE !== EnvTypes.PROD,
  });

  store.asyncReducers = {};

  // This function adds the async reducer, and creates a new combined reducer
  store.injectReducer = (key, asyncReducer, initialData) => {
    if (reducerManager.removable(key)) {
      logInDev(`Found existing reducer ${key}`);
      return;
    }

    // Inject new reducer
    reducerManager.add(key, asyncReducer, initialData);
    store.replaceReducer(reducerManager.getCombinedReducers());
  };

  // This function adds the async reducer, and creates a new combined reducer
  store.injectReducers = (reducers) => {
    Object.entries(reducers).forEach(([key, value]) => {
      store.injectReducer(key, value);
    });
  };

  store.removeReducer = (key) => {
    if (reducerManager.removable(key)) {
      reducerManager.remove(key);
      store.replaceReducer(reducerManager.getCombinedReducers());
    }
  };

  store.removeReducers = (reducers) => {
    Object.entries(reducers).forEach(([key]) => {
      store.removeReducer(key);
    });
  };

  store.injectSaga = createSagaInjector(sagaMiddleware.run, rootSaga);

  store.hasReducer = (key) => reducerManager.has(key);

  SliceManager.init(store);

  store.injectSlice = SliceManager.injectSlice;

  store.removeSlice = SliceManager.removeSlice;

  store.hasSlice = SliceManager.hasSlice;

  return store;
};
